import trackingController, { type TrackingEntity } from './trackingController';
import waypointQueue, { TERMINAL_UPLOAD_CODES, type UploadAllResult } from './waypointQueue';
import jobService from './jobs';
import sprayService from './spray';

export interface FinishUpload extends UploadAllResult {
  /** Put GPS capture back the way it was — call when the finish does not go ahead. */
  restore: () => Promise<void>;
}

/**
 * Get every GPS point for a job or spray off the phone before it is finished.
 *
 * Once finished, the server refuses its points for good and the queue throws
 * them away, so a backlog built up out of signal has to be uploaded first —
 * otherwise driving back into range and signing off straight away loses most
 * of the track, and with it the coverage map and the per-tank checks.
 *
 * Capture is paused first so no fix lands between the last upload and the
 * finish. A job or spray paused on the server refuses points (a non-terminal
 * 409); it is resumed and the upload retried, which is the same thing the
 * tracker's Retry button does, and harmless because it is finished next.
 *
 * When `remaining` is above zero the caller should not finish without asking.
 */
export async function uploadBeforeFinish(
  entity: TrackingEntity,
  onProgress?: (remaining: number) => void,
): Promise<FinishUpload> {
  const state = trackingController.getState();
  const wasCapturing =
    state.tracking && state.entity?.type === entity.type && state.entity.id === entity.id;
  if (wasCapturing) await trackingController.pauseCapture();

  const restore = async () => {
    if (wasCapturing) await trackingController.start(entity).catch(() => {});
  };

  let result = await waypointQueue.uploadAll(entity.type, entity.id, onProgress);
  const err = result.error;
  if (result.remaining > 0 && err?.status === 409 && !TERMINAL_UPLOAD_CODES.has(err.code ?? '')) {
    try {
      if (entity.type === 'job') await jobService.resumeJob(entity.id);
      else await sprayService.resumeApplication(entity.id);
      const retry = await waypointQueue.uploadAll(entity.type, entity.id, onProgress);
      result = { ...retry, uploaded: result.uploaded + retry.uploaded };
    } catch (e) {
      console.warn('[finishTracking] resume before upload failed', e);
    }
  }

  return { ...result, restore };
}

/** Stop GPS capture after a finish, but only if it is this entity's. */
export async function stopTrackingFor(entity: TrackingEntity): Promise<void> {
  const cur = trackingController.getState().entity;
  if (cur?.type === entity.type && cur.id === entity.id) await trackingController.stop();
}

/** Plain-language reason a finishing upload stopped short. */
export function describeUploadFailure(r: UploadAllResult): string {
  const n = `${r.remaining} GPS point${r.remaining === 1 ? '' : 's'}`;
  const status = r.error?.status;
  if (status == null) {
    return `${n} are still on this phone and could not be uploaded — there is no connection. ` +
      'Move into signal and try again. Finishing now would lose them.';
  }
  if (status === 401) {
    return `${n} are still on this phone, but your session has expired. ` +
      'Sign in again and then finish. Finishing now would lose them.';
  }
  return `${n} are still on this phone and the server would not take them` +
    `${r.error?.message ? ` (${r.error.message})` : ''}. Finishing now would lose them.`;
}
